<template>
    <VJoyPanel :id="anchorsPageProfile.industryExpertiseSection" data-testid="profile-section-industry-expertises">
        <template #panel-title>
            <h2>{{ t('profile.section.industry.expertise.title') }}</h2>
        </template>
        <template #panel-title-action>
            <VJoyIconButton
                v-if="isEditModeEnabled"
                variant="ghost"
                icon="pen"
                size="small"
                data-testid="profile-section-industry-expertises-cta-edit"
                :label="$t('general.edit')"
                @click="openIndustryDialog"
            />
        </template>

        <template #panel-body>
            <FetchErrorHighlight v-if="hasFetchError" />
            <ShowMoreByMaxHeight
                v-else
                :label="t('profile.section.expertises.show.all.label.v2', {count: industryExpertises?.length ?? 0})"
                :max-height="showMoreMaxHeight"
                :locked="{
                    allowHref: true,
                    label: t('profile.section.expertises.private', [profile?.firstname]),
                    componentName: 'profile-industry-expertise-section',
                    targetId: anchorsPageProfile.industryExpertiseSection,
                }"
            >
                <SkillsReadOnly
                    v-if="hasIndustryExpertises"
                    data-testid="profile-section-industry-expertises"
                    :skills="
                        industryExpertises?.map((industryExpertise: ProfileIndustryExpertise) => ({
                            label: industryExpertise.label,
                            seoUrl: industryExpertise.seoUrl,
                            id: industryExpertise.id,
                        })) ?? []
                    "
                    :is-visible-seo-link="isVisibleSeoLink"
                />
                <div v-else data-testid="profile-section-industry-expertises-empty-state">
                    {{ t('profile.section.industry.expertise.empty.state.placeholder') }}
                </div>
            </ShowMoreByMaxHeight>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useAuth, useTranslation} from '#imports';
    import {VJoyIconButton, VJoyPanel} from '@maltjoy/core-vue';
    import FetchErrorHighlight from '~/components/_common/FetchErrorHighlight.vue';
    import ShowMoreByMaxHeight from '~/components/_common/show-more/ShowMoreByMaxHeight.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {ProfileVertical} from '~/types/domain/Profile';
    import type {ProfileIndustryExpertise} from '~/types/domain/ProfileExpertises';
    import SkillsReadOnly from '@/components/_common/skills/SkillsReadOnly.vue';

    const {t} = useTranslation();
    const {profile, industryExpertises, profileFetchErrors} = storeToRefs(useProfileStore());
    const {loggedIn} = storeToRefs(useAuth());

    const isEditModeEnabled = computed(() => profile.value?.editMode || false);

    const hasIndustryExpertises = computed(() => industryExpertises.value!.length > 0);
    const hasFetchError = computed(() => profileFetchErrors.value.expertises);
    const isVisibleSeoLink = computed(() => profile.value?.vertical === ProfileVertical.FREELANCER);

    const showMoreMaxHeight = loggedIn ? 400 : 180;

    function openIndustryDialog() {
        if (profile.value!.editMode) {
            eventBus.emit('industry-expertise:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
        }
    }
</script>
